import {
  classifyPurchaseError,
  getChinaAndroidTrialProductId,
  tagFailure,
  waitForSubscriptionStatus,
  withFailure,
} from '@forest/paywall-core'
import { useMutation } from '@tanstack/react-query'
import { Howler } from 'howler'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { m } from '~/paraglide/messages'
import { api } from '~/services/api'
import { native } from '@forest/native-services'
import { setChinaAndroidTrialState } from '~/store/china-android-trial'
import { dialog } from '~/store/dialog'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'

const CHINA_ANDROID_TRIAL_PRODUCT_ID = getChinaAndroidTrialProductId(
  import.meta.env.MODE,
)

export function useChinaAndroidTrialMutation(params?: {
  onSuccess?: () => Promise<unknown> | unknown
}) {
  const { onSuccess } = params ?? {}

  return useMutation({
    mutationFn: async () => {
      const userInfo = await getAuthenticatedUserInfo().catch(
        (error: unknown) => {
          throw tagFailure(error, 'auth')
        },
      )

      await api.service
        .post(`store/users/${userInfo.user_id}/purchase-right`, {
          json: {
            product_id: CHINA_ANDROID_TRIAL_PRODUCT_ID,
            iap_sku: 'cna_plus_trial_7',
          },
        })
        .catch((error: unknown) => {
          throw tagFailure(error, 'create_order')
        })

      try {
        await waitForSubscriptionStatus({
          serviceApi: api.service,
          userId: userInfo.user_id,
        })
      } catch {
        // Assume subscribed if polling fails; handled by subsequent refresh
      }
    },
    onSuccess: async () => {
      setChinaAndroidTrialState('unavailable')
      native.user.refreshServiceLevel()
      Howler.stop()
      await onSuccess?.()
    },
    onError: (error) => {
      if (error instanceof AbortError) return

      const intent = classifyPurchaseError(error)
      switch (intent.kind) {
        case 'no_internet':
          dialog.open({
            title: m.dialog_no_internet_title(),
            description: withFailure(
              m.dialog_no_internet_content(),
              intent.failure,
            ),
            button: m.OK(),
          })
          return
        case 'subscription_conflict':
          void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
          return
        case 'payment_timeout':
        case 'illegal_transaction_restore':
        case 'http_error':
        case 'unknown':
          dialog.open({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            button: m.contact_cs(),
            action: () => {
              openIntercom({
                node:
                  intent.kind === 'http_error'
                    ? 'china_android_trial_unexpected_http_error'
                    : 'china_android_trial_unexpected_error',
                additional: { error_code: intent.failure.code },
              })
            },
          })
          return
        default:
          intent satisfies never
      }
    },
  })
}
